/**
 * Public privacy policy. Reachable at /privacy without auth so the
 * URL can be linked from the App Store listing and the iOS app's
 * help screen.
 *
 * Plain-text legal copy — keep accessible (h2 hierarchy, real <a> for
 * the contact email) so screen readers and Apple's automated checks
 * read it cleanly.
 */
export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      <div className="max-w-2xl mx-auto px-5 py-10 space-y-6">
        <header className="space-y-2">
          <a href="/" className="text-sm text-brand-muted hover:text-brand-text">← Back</a>
          <h1 className="font-display text-3xl font-black">Privacy Policy</h1>
          <p className="text-sm text-brand-muted">Last updated: April 27, 2026</p>
        </header>

        <section className="space-y-3 text-sm leading-relaxed">
          <p>
            Jordan Draper Games (&ldquo;JDG,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) builds the
            mobile and web games at <strong>jordandraper.com</strong> and on the App Store as
            &ldquo;Jordan Draper Games.&rdquo; This policy explains what we collect, why, and the
            choices you have.
          </p>
          <p>
            We do not sell your personal information. We do not share it with advertising
            networks. The only third parties that ever see your data are the operational
            services listed below, and they only see what they need to do their job.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">What we collect</h2>
          <div className="space-y-2 text-sm leading-relaxed">
            <p><strong>Account data.</strong> When you sign up we store your email address,
              chosen display name, and (optionally) a profile photo you upload. If you sign in
              with Apple, we receive an opaque identifier from Apple plus the email address you
              choose to share.</p>
            <p><strong>Gameplay data.</strong> The games store the things you submit and the
              actions you take inside them: weekly story submissions, votes, color picks,
              trophies earned, and the rooms you played in. This is the content of the
              service; without it the games can&rsquo;t run.</p>
            <p><strong>Subscription data.</strong> If you subscribe to All-Access, we store
              your subscription status, the platform you bought through (Apple, Google, or
              Stripe), and the renewal date. We never see or store your card number. Payment
              processors handle that on their own systems.</p>
            <p><strong>Technical data.</strong> Our hosting provider (Supabase) records
              standard server logs including IP addresses, request times, and error traces.
              These are kept for security and debugging and are deleted on a rolling basis.</p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">How we use it</h2>
          <ul className="list-disc pl-5 space-y-1 text-sm leading-relaxed">
            <li>To operate the games and weekly events you participate in.</li>
            <li>To attribute submissions, votes, and trophies to the correct profile.</li>
            <li>To process subscription purchases and grant access to paid features.</li>
            <li>To respond when you contact support.</li>
            <li>To prevent abuse and keep the service running.</li>
          </ul>
          <p className="text-sm leading-relaxed">
            We do not use your data for advertising, profiling, or training third-party AI
            models.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">Service providers</h2>
          <ul className="list-disc pl-5 space-y-1 text-sm leading-relaxed">
            <li><strong>Supabase</strong>: database, authentication, file storage, and
              server-side functions. All app data is stored on Supabase&rsquo;s managed
              Postgres infrastructure.</li>
            <li><strong>Apple In-App Purchase</strong> (iOS): handles your subscription
              purchase if you buy through the iOS app. Receipts are processed via RevenueCat.</li>
            <li><strong>Google Play Billing</strong> (future Android release): same role on
              Android.</li>
            <li><strong>Stripe</strong> (web only): processes web subscriptions when bought
              from a desktop browser. Subject to Stripe&rsquo;s own privacy policy.</li>
            <li><strong>RevenueCat</strong>: receives store webhooks and forwards
              subscription state to our servers so the app knows your access level.</li>
            <li><strong>Vercel</strong>: hosts the web build of the app.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">Children</h2>
          <p className="text-sm leading-relaxed">
            JDG is not directed at children under 13 (or under 16 in the EEA / UK). If you
            believe a child has signed up, contact us and we&rsquo;ll remove the account.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">Your choices</h2>
          <ul className="list-disc pl-5 space-y-1 text-sm leading-relaxed">
            <li><strong>Edit your profile</strong> at any time from Profile → Edit profile.</li>
            <li><strong>Cancel your subscription</strong> from your platform&rsquo;s
              subscription manager: iOS → Settings → Apple ID → Subscriptions; Android →
              Play Store → Subscriptions; web → the Stripe Customer Portal linked from the
              app.</li>
            <li><strong>Close your account</strong> from Profile → Privacy → Close my
              account. We soft-delete your profile (sign-in is disabled, photo removed).
              We retain your past weekly submissions and votes under your chosen display name
              so other players&rsquo; archives stay intact. These are public game records,
              not private data.</li>
            <li><strong>Request your data or deletion</strong> beyond the in-app flow by
              emailing us at the address below. We respond within 30 days.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">Data security</h2>
          <p className="text-sm leading-relaxed">
            Authentication is handled by Supabase Auth with industry-standard hashing.
            All traffic between the app and our servers is encrypted in transit (HTTPS / TLS).
            Database access is limited via row-level security so each user can only read and
            write their own data, except for public game records (your display name on a
            weekly leaderboard, for example).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">International transfers</h2>
          <p className="text-sm leading-relaxed">
            Our service providers operate in the United States. By using JDG you consent to
            your data being transferred there for processing.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">Changes to this policy</h2>
          <p className="text-sm leading-relaxed">
            If we make material changes, we&rsquo;ll update the &ldquo;last updated&rdquo;
            date above and, when the change is significant, surface a notice in the app.
            Continued use after a change means you accept the updated policy.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-bold">Contact</h2>
          <p className="text-sm leading-relaxed">
            Questions, deletion requests, or anything else:{' '}
            <a
              href="mailto:talk@jordandraper.com"
              className="text-brand-lime-dark underline"
            >
              talk@jordandraper.com
            </a>
          </p>
        </section>
      </div>
    </div>
  )
}
